import styled from 'styled-components';
import { Colors } from 'src/utils/color';
import { Button } from 'src/shared';

// Global font family for styled components
const fontFamily = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";

export const LoginContainer = styled.div`
    width: 100%;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background-color: ${Colors.theme.sidebar_background};
    font-family: ${fontFamily};
`;

export const TopNavBar = styled.nav`
    width: 100%;
    background-color: ${Colors.theme.page_background};
    padding: 16px 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
    z-index: 100;
    font-family: ${fontFamily};

    @media (max-width: 768px) {
        padding: 12px 20px;
    }
`;

export const NavLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

export const NavLogo = styled.div`
    font-size: 18px;
    font-weight: 700;
    color: ${Colors.theme.primary};
    font-family: ${fontFamily};
`;

export const NavLinks = styled.div`
    display: flex;
    align-items: center;
    gap: 24px;
    margin-left: 32px;

    @media (max-width: 768px) {
        display: none;
    }

    a {
        color: ${Colors.extra.dark_gray};
        text-decoration: none;
        font-size: 14px;
        font-weight: 500;
        font-family: ${fontFamily};
        transition: color 0.2s ease;

        &:hover {
            color: ${Colors.theme.primary};
        }
    }
`;

export const NavButton = styled.button`
    background-color: ${Colors.theme.primary};
    color: white;
    border: none;
    border-radius: 8px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    font-family: ${fontFamily};
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.9;
    }
`;

export const MainContent = styled.div`
    flex: 1;
    display: flex;
    width: 100%;
    min-height: calc(100vh - 80px);
`;

export const LeftSection = styled.div`
    flex: 0 0 60%;
    background-color: ${Colors.theme.page_background};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 80px;
    font-family: ${fontFamily};

    @media (max-width: 1024px) {
        flex: 0 0 100%;
        padding: 40px;
    }

    @media (max-width: 768px) {
        padding: 30px 20px;
    }
`;

export const RightSection = styled.div`
    flex: 0 0 40%;
    background: linear-gradient(135deg, ${Colors.theme.primary} 0%, #1E6BCF 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
    font-family: ${fontFamily};

    @media (max-width: 1024px) {
        display: none;
    }

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-image: 
            radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
        opacity: 0.5;
    }
`;

export const BrandContent = styled.div`
    position: relative;
    z-index: 1;
    text-align: center;
    color: white;
    font-family: ${fontFamily};
`;

export const BrandLogo = styled.div`
    font-size: 64px;
    font-weight: 700;
    margin-bottom: 16px;
    font-family: ${fontFamily};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
`;

export const BrandImage = styled.img`
    max-width: 280px;
    max-height: 120px;
    width: auto;
    height: auto;

    @media (max-width: 1200px) {
        max-width: 220px;
        max-height: 96px;
    }
`;

export const BrandText = styled.div`
    font-size: 24px;
    font-weight: 500;
    opacity: 0.95;
    font-family: ${fontFamily};
`;

export const LoginFormContainer = styled.div`
    width: 100%;
    max-width: 450px;
    font-family: ${fontFamily};
`;

export const WelcomeTitle = styled.h1`
    font-size: 32px;
    font-weight: 700;
    color: ${Colors.theme.primary};
    margin: 0 0 8px 0;
    font-family: ${fontFamily};
`;

export const WelcomeSubtitle = styled.p`
    font-size: 16px;
    color: ${Colors.extra.dark_gray};
    margin: 0 0 40px 0;
    font-family: ${fontFamily};
`;

export const FormWrapper = styled.div`
    width: 100%;
    font-family: ${fontFamily};
`;

export const FormField = styled.div`
    margin-bottom: 24px;
    font-family: ${fontFamily};
`;

export const RememberMeWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 32px;
    font-family: ${fontFamily};
`;

export const RememberMe = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: ${fontFamily};

    label {
        font-size: 14px;
        color: ${Colors.extra.dark_gray};
        cursor: pointer;
        font-family: ${fontFamily};
        user-select: none;
    }
`;

export const SignInButton = styled(Button)`
    width: 100%;
    padding: 14px;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 24px;
    font-family: ${fontFamily};
`;

export const SignUpLink = styled.div`
    text-align: center;
    font-size: 14px;
    color: ${Colors.extra.dark_gray};
    font-family: ${fontFamily};

    a {
        color: ${Colors.theme.primary};
        text-decoration: none;
        font-weight: 600;
        margin-left: 4px;
        font-family: ${fontFamily};

        &:hover {
            text-decoration: underline;
        }
    }
`;

export const ForgotPassword = styled.div`
    text-align: center;
    margin-top: 16px;
    font-family: ${fontFamily};

    a {
        color: ${Colors.theme.primary};
        text-decoration: none;
        font-size: 14px;
        font-weight: 500;
        font-family: ${fontFamily};
        
        &:hover {
            text-decoration: underline;
        }
    }
`;

export const StyledLabel = styled.label`
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: ${Colors.extra.dark_gray};
    margin-bottom: 8px;
    font-family: ${fontFamily};
`;

export const StyledInput = styled.input`
    width: 100%;
    height: 48px;
    padding: 12px 16px;
    border: 1px solid ${Colors.theme.sidebar_border};
    border-radius: 8px;
    font-size: 16px;
    font-family: ${fontFamily};
    color: ${Colors.extra.black};
    background-color: ${Colors.theme.page_background};
    outline: none;
    transition: border-color 0.2s ease;

    &:focus {
        border-color: ${Colors.theme.primary};
    }

    &::placeholder {
        color: ${Colors.extra.dark_gray};
        opacity: 0.6;
    }
`;

export const StyledSwitch = styled.input`
    width: 40px;
    height: 20px;
    appearance: none;
    background-color: ${Colors.extra.light_gray};
    border-radius: 20px;
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:checked {
        background-color: ${Colors.theme.primary};
    }

    &::before {
        content: '';
        position: absolute;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background-color: white;
        top: 2px;
        left: 2px;
        transition: transform 0.2s ease;
        box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
    }

    &:checked::before {
        transform: translateX(20px);
    }
`;
